import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const DOMAIN_WARP_PARAMS = [
  {
    kind: 'float',
    key: 'strength',
    label: 'Warp-Stärke',
    min: 0,
    max: 400,
    step: 1,
    default: 55,
    unit: 'px',
    bulkRange: [10, 180],
    group: 'Verzerrung',
  },
  {
    kind: 'int',
    key: 'passes',
    label: 'Durchgänge',
    min: 1,
    max: 4,
    step: 1,
    default: 2,
    group: 'Verzerrung',
    tooltip: 'Jeder Durchgang verzerrt das Ergebnis des vorherigen — das ergibt die typischen Wirbel.',
  },
  {
    kind: 'float',
    key: 'noiseScale',
    label: 'Noise-Skala',
    min: 0.0002,
    max: 0.02,
    step: 0.0001,
    scale: 'log',
    default: 0.0022,
    precision: 4,
    unit: '1/px',
    group: 'Verzerrung',
  },
  {
    kind: 'int',
    key: 'octaves',
    label: 'Oktaven',
    min: 1,
    max: 6,
    step: 1,
    default: 3,
    group: 'Verzerrung',
  },
  {
    kind: 'float',
    key: 'lacunarity',
    label: 'Lacunarity',
    min: 1.2,
    max: 4,
    step: 0.05,
    default: 2,
    group: 'Verzerrung',
    tooltip: 'Frequenzsprung zwischen Oktaven.',
    showIf: (v) => (v.octaves as number) > 1,
  },
  {
    kind: 'float',
    key: 'gain',
    label: 'Gain',
    min: 0.1,
    max: 0.9,
    step: 0.01,
    default: 0.5,
    group: 'Verzerrung',
    tooltip: 'Amplitudenabfall zwischen Oktaven.',
    showIf: (v) => (v.octaves as number) > 1,
  },
  {
    kind: 'float',
    key: 'scalePerPass',
    label: 'Skala je Durchgang',
    min: 0.3,
    max: 3,
    step: 0.05,
    default: 1.8,
    group: 'Verzerrung',
    showIf: (v) => (v.passes as number) > 1,
  },
  {
    kind: 'vec2',
    key: 'bias',
    label: 'Richtungs-Bias',
    min: { x: -1, y: -1 },
    max: { x: 1, y: 1 },
    step: 0.01,
    default: { x: 0, y: 0 },
    group: 'Verzerrung',
    tooltip: 'Zieht die Verzerrung bevorzugt in eine Richtung.',
  },
  {
    kind: 'float',
    key: 'legibility',
    label: 'Lesbarkeit erhalten',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.4,
    group: 'Verzerrung',
    tooltip: 'Dämpft die Verzerrung im Kern der Buchstaben, sodass die Form erkennbar bleibt.',
  },
  {
    kind: 'float',
    key: 'legibilityReach',
    label: 'Kern-Reichweite',
    min: 2,
    max: 200,
    step: 1,
    scale: 'log',
    default: 34,
    unit: 'px',
    group: 'Verzerrung',
    showIf: (v) => (v.legibility as number) > 0,
  },

  {
    kind: 'int',
    key: 'copies',
    label: 'Wiederholungen',
    min: 1,
    max: 40,
    step: 1,
    // A single warped outline is a thin curiosity; a stack of increasingly warped
    // copies is what makes the technique read as a field pulling the letters apart.
    default: 7,
    bulkRange: [3, 16],
    group: 'Darstellung',
    tooltip: 'Zeichnet mehrere unterschiedlich stark verzerrte Kopien übereinander.',
  },
  {
    kind: 'enum',
    key: 'style',
    label: 'Stil',
    default: 'stroke',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'fill', label: 'Füllung' },
      { value: 'stroke', label: 'Kontur' },
    ],
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.2,
    max: 20,
    step: 0.1,
    default: 1.6,
    unit: 'px',
    group: 'Darstellung',
    showIf: (v) => v.style === 'stroke',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.03,
    max: 1,
    step: 0.01,
    default: 0.55,
    group: 'Darstellung',
  },

  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'ember' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type DomainWarpValues = SchemaValues<typeof DOMAIN_WARP_PARAMS>;
